import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Recording an ad from the camera while the script rolls. Carried over from Flashkut's teleprompter
 * (frontend/src/components/teleprompter/useMediaRecorder.ts) without its crop and zoom: an ad keeps the
 * camera's own frame, upright on a phone, wide on a laptop. It records MP4, which goes to Meta unchanged.
 */

// Why MP4 (H.264) only: Meta ads take MP4 and MOV, not WebM (Zernio's Meta Ads docs, read 7 Oct 2026), and
// Flashadz converts nothing. Chrome, Edge and Safari record it; Firefox records only WebM, so it cannot record here.
const TYPES = ["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4;codecs=avc1,mp4a.40.2", "video/mp4;codecs=avc1", "video/mp4"];

/** Whether this browser can record a video Meta takes. */
export const canRecordHere = () =>
  typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices?.getUserMedia && TYPES.some((t) => MediaRecorder.isTypeSupported(t));

export type RecorderState = "off" | "starting" | "ready" | "recording" | "done";

/** One finished take: the file, a link to play it here, how long it is, and whether the frame is wide. */
export interface Take {
  file: File;
  url: string;
  seconds: number;
  wide: boolean;
}

function cameraProblem(e: unknown): string {
  const name = e instanceof DOMException ? e.name : "";
  if (name === "NotAllowedError") return "Allow the camera and microphone for this page, then try again.";
  if (name === "NotFoundError") return "No camera was found.";
  if (name === "NotReadableError") return "Another app is using the camera. Close it, then try again.";
  return "The camera did not start. Try again.";
}

export function useRecorder() {
  const [state, setState] = useState<RecorderState>("off");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [take, setTake] = useState<Take | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const mounted = useRef(true);
  const opening = useRef(false);
  const current = useRef<MediaStream | null>(null);
  useEffect(() => {
    current.current = stream;
  }, [stream]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const open = useCallback(async () => {
    // Why: one camera at a time; a second ask while one is open or opening would leave a stream nobody stops.
    if (opening.current || current.current) return;
    opening.current = true;
    setError(null);
    setState("starting");
    try {
      // Why: always the camera's own wide size, never an upright one. Android Chrome reads sizes in the camera's
      // sideways frame, so an upright ask cuts a strip that comes out wide and zoomed; asked wide, a phone held
      // upright turns the whole frame upright (Flashkut's teleprompter learned this, useMediaRecorder.ts).
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      // Why: the owner can close the reader while the browser still asks; a camera arriving then is let go at once.
      if (!mounted.current) {
        for (const t of s.getTracks()) t.stop();
        return;
      }
      setStream(s);
      setState("ready");
    } catch (e) {
      setError(cameraProblem(e));
      setState("off");
    } finally {
      opening.current = false;
    }
  }, []);

  const start = useCallback(() => {
    if (!stream) return;
    const type = TYPES.find((t) => MediaRecorder.isTypeSupported(t));
    // Why: a camera the phone took back (a call, another app) has ended; the owner turns it on again.
    if (!type || stream.getVideoTracks().some((t) => t.readyState === "ended")) {
      for (const t of stream.getTracks()) t.stop();
      setStream(null);
      setState("off");
      setError("The camera stopped. Turn it on again to record.");
      return;
    }
    let r: MediaRecorder;
    try {
      r = new MediaRecorder(stream, { mimeType: type });
    } catch {
      setError("Recording did not start. Try again.");
      return;
    }
    chunks.current = [];
    r.ondataavailable = (e) => {
      if (e.data.size) chunks.current.push(e.data);
    };
    r.onstop = () => {
      const ms = Date.now() - startedAt.current;
      const blob = new Blob(chunks.current, { type: "video/mp4" });
      chunks.current = [];
      const s = stream.getVideoTracks()[0]?.getSettings();
      const file = new File([blob], "take.mp4", { type: "video/mp4" });
      const url = URL.createObjectURL(file);
      const guess = !!s?.width && !!s.height && s.width > s.height;
      // Why: the file's own frame says whether the take is wide or upright; the camera's settings are the fallback.
      const probe = document.createElement("video");
      let settled = false;
      const settle = (wide: boolean) => {
        if (settled) return;
        settled = true;
        probe.removeAttribute("src");
        if (!mounted.current) return URL.revokeObjectURL(url);
        setTake({ file, url, seconds: Math.round(ms / 1000), wide });
        setState("done");
      };
      probe.muted = true;
      probe.preload = "metadata";
      probe.addEventListener("loadedmetadata", () => settle(probe.videoWidth && probe.videoHeight ? probe.videoWidth > probe.videoHeight : guess), { once: true });
      probe.addEventListener("error", () => settle(guess), { once: true });
      setTimeout(() => settle(guess), 3000);
      probe.src = url;
    };
    recorder.current = r;
    startedAt.current = Date.now();
    try {
      r.start(1000);
    } catch {
      setError("Recording did not start. Try again.");
      return;
    }
    setError(null);
    setState("recording");
  }, [stream]);

  const stop = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);

  const again = useCallback(() => {
    setTake(null);
    setState("ready");
  }, []);

  // Why: a camera left on keeps its light lit and other apps locked out; it goes the moment the reader closes.
  useEffect(
    () => () => {
      if (recorder.current?.state === "recording") recorder.current.stop();
      for (const t of stream?.getTracks() ?? []) t.stop();
    },
    [stream],
  );
  useEffect(() => () => void (take && URL.revokeObjectURL(take.url)), [take]);

  return { state, stream, error, take, open, start, stop, again };
}
